import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../store/hooks';
import { selectCurrentApplication } from '../../store/slices/application.slice';
import MainLayout from '../../components/layout/MainLayout';
import TimeRangeSelector, { TimeRange } from '../../components/common/TimeRangeSelector';
import ApplicationSelector from '../../components/common/ApplicationSelector';
import productAnalyticsService from '../../services/api/product-analytics.service';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const CHART_COLORS = ['#06b6d4', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#6366f1', '#14b8a6'];

export default function TrafficPage() {
  const { t } = useTranslation('analytics');
  const selectedAppId = useAppSelector(selectCurrentApplication);
  const [timeRange, setTimeRange] = useState<TimeRange>({
    startTime: Date.now() - 7 * 86400000, endTime: Date.now(), rangeType: 'preset', presetValue: '7d',
  });
  const [loading, setLoading] = useState(false);
  const [trafficData, setTrafficData] = useState<Array<{ source: string; pageViews: number; sessions: number }>>([]);

  const appId = selectedAppId || 'all';
  const startISO = new Date(timeRange.startTime).toISOString();
  const endISO = new Date(timeRange.endTime).toISOString();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await productAnalyticsService.getTrafficSources(appId, startISO, endISO);
      setTrafficData(data || []);
    } catch (err) {
      console.error('Failed to load traffic data:', err);
    } finally {
      setLoading(false);
    }
  }, [appId, startISO, endISO]);

  useEffect(() => { loadData(); }, [loadData]);

  const totalSources = trafficData.length;
  const totalViews = trafficData.reduce((s, d) => s + d.pageViews, 0);
  const directTraffic = trafficData.find(d => d.source === 'Direct');
  const directPercent = totalViews > 0 && directTraffic ? ((directTraffic.pageViews / totalViews) * 100).toFixed(1) : '0';
  const topReferrer = trafficData.filter(d => d.source !== 'Direct')[0]?.source || '-';

  const fmt = (n: number) => { if (n >= 1e6) return `${(n/1e6).toFixed(1)}M`; if (n >= 1e3) return `${(n/1e3).toFixed(1)}K`; return n.toString(); };
  const pct = (n: number) => totalViews > 0 ? ((n / totalViews) * 100).toFixed(1) : '0.0';

  const pieData = trafficData.slice(0, 8).map(d => ({ name: d.source, value: d.pageViews }));

  return (
    <MainLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('traffic.title')}</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{t('traffic.subtitle')}</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">{t('traffic.filterBySite')}</span>
              <ApplicationSelector />
            </div>
            <div className="flex-1">
              <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
            </div>
          </div>
        </div>

        {/* 3 Stat Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('traffic.totalSources')}</p>
            <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{totalSources}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('traffic.directPercent')}</p>
            <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{directPercent}%</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('traffic.topReferrer')}</p>
            <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white truncate">{topReferrer}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* PieChart */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('traffic.sourcesDistribution')}</h2>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={110} dataKey="value" paddingAngle={2}>
                    {pieData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : <div className="text-center py-12 text-gray-500">{t('traffic.noData')}</div>}
          </div>

          {/* Top Referrers Table */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('traffic.topReferrers')}</h2>
            {trafficData.length === 0 ? (
              <div className="text-center py-12 text-gray-500">{t('traffic.noData')}</div>
            ) : (
              <div className="space-y-3">
                {trafficData.map((item, i) => (
                  <div key={item.source} className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2 min-w-0">
                        <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}></div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{item.source}</span>
                      </div>
                      <div className="flex items-center space-x-3 text-sm text-gray-500 shrink-0">
                        <span>{fmt(item.pageViews)} {t('traffic.pageViews').toLowerCase()}</span>
                        <span className="text-xs">{pct(item.pageViews)}%</span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                      <div className="h-1.5 rounded-full transition-all" style={{ width: `${parseFloat(pct(item.pageViews))}%`, backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
